import { NetworkStatus, useQuery } from '@apollo/client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useList } from 'react-use';

import type { ChangeLogEntryEdge, GetChangeLogByAdObjectIdQuery } from '../../../apis/graphql';
import { EntityType } from '../../../pages/Trafficking/TraffickingPage/hooks/makePageDrawerPlugin';
import {
  ChangesPrefixes,
  DEFAULT_AMOUNT_OF_LOGS_TO_SHOW,
  LIMIT_AUDITS,
  NOTIFY_ON_NETWORK_STATUS_CHANGE,
} from '../constants';
import type { ChangeLogUnionObjectWithTerms } from '../types';
import { transformTargetingToSameType } from '../utils';
import { GET_CHANGE_LOG } from './queries';
import type {
  ChangeLogEntryNode,
  GetChangeLogByObjectIdQueryVariables,
  GetVariables,
  GetVariablesResult,
  NestedChangesPrefix,
  TargetingChanges,
  TargetingChangesList,
  UseChangeLogProps,
  UseChangeLogResult,
} from './types';
import { useChangeLogList } from './useChangeLogList';
import { useDateRangeFilter } from './useDateRangeFilter';
import { usePagination } from './usePagination';

const getVariables = ({ id, entityType }: GetVariables): GetVariablesResult => {
  const defaultVariables = {
    id,
    paginationOptions: {
      limit: LIMIT_AUDITS,
      offset: 0,
    },
  };

  return entityType === 'Ad'
    ? {
        entityType: 'AD',
        ...defaultVariables,
      }
    : {
        ...defaultVariables,
      };
};

export default function useChangeLog({ entityType, id }: UseChangeLogProps): UseChangeLogResult {
  const [desiredItemsToShow, setDesiredItemsToShow] = useState(DEFAULT_AMOUNT_OF_LOGS_TO_SHOW);
  const [changeLogEntries, setChangeLogEntries] = useState<ChangeLogEntryNode[]>([]);
  const [loadingFilter, setLoadingFilter] = useState(false);
  const nestedChangesPrefix: NestedChangesPrefix =
    entityType === EntityType.AD ? ChangesPrefixes.AdRotation : undefined;

  const [queryVariables, setQueryVariables] = useState<GetChangeLogByObjectIdQueryVariables>({
    ...getVariables({ id, entityType }),
  });

  const { data, loading, networkStatus } = useQuery<GetChangeLogByAdObjectIdQuery>(GET_CHANGE_LOG, {
    variables: {
      ...queryVariables,
    },
    skip: !id,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: NOTIFY_ON_NETWORK_STATUS_CHANGE,
    onCompleted: () => {
      setLoadingFilter(false);
    },
  });

  const total = data?.getChangeLogByAdObjectID?.total ?? 0;

  useEffect(() => {
    if (networkStatus === NetworkStatus.ready) {
      setChangeLogEntries(data?.getChangeLogByAdObjectID?.edges as ChangeLogEntryNode[]);
    }
  }, [data, networkStatus]);

  const [toggles, { set, updateAt, update }] = useList<boolean | boolean[]>([]);

  useEffect(() => {
    setQueryVariables(getVariables({ id, entityType }));
    setChangeLogEntries([]);
    setDesiredItemsToShow(DEFAULT_AMOUNT_OF_LOGS_TO_SHOW);
    setLoadingFilter(false);
    set([]);
  }, [id, entityType, set]);

  useEffect(() => {
    const mixedTogglesWithCreatives = changeLogEntries?.map((change) => {
      if (change?.node.creativeAudits?.length) {
        const creativesArr = new Array(change?.node.creativeAudits.length).fill(false);
        return [false, ...creativesArr];
      }

      return false;
    });

    if (changeLogEntries && !loading && changeLogEntries.length) {
      set(toggles.length ? [...toggles, ...mixedTogglesWithCreatives] : [...mixedTogglesWithCreatives]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changeLogEntries, loading, set]);

  const toggleAt = useCallback(
    (index: number, value: boolean | boolean[]) => {
      const newValue = value === undefined ? !toggles[index] : value;
      updateAt(index, Array.isArray(value) ? value.map((val) => !val) : !newValue);
    },
    [toggles, updateAt]
  );

  const toggleAll = useCallback(
    (isOpen: boolean | boolean[]) => {
      update((isOpen) => Array.isArray(isOpen), [isOpen, isOpen] as boolean[]);
      update((isOpen) => !Array.isArray(isOpen), isOpen);
    },

    [update]
  );

  const { dateRange, onDateRangeChange, onFilterSubmit, onClearFilter } = useDateRangeFilter({
    setQueryVariables,
    id,
    setLoadingFilter,
    toggleAll,
    setDesiredItemsToShow,
  });

  const transformedChangeLogEntries = useMemo(() => transformTargetingToSameType(changeLogEntries), [changeLogEntries]);

  let targetingChangesList: TargetingChangesList = [];

  try {
    targetingChangesList = mapTargetingChanges(data);
  } catch (error) {
    console.error('processTargetingChanges failure', {
      error,
      changeLogEntries,
    });
  }

  const { filteredChangeLogList } = useChangeLogList({
    changeLogEntries: transformedChangeLogEntries,
    targetingChangesList,
    nestedChangesPrefix,
    entityType,
  });

  const { filteredLogsToShow, hasMore, onNext } = usePagination({
    filteredChangeLogList,
    changeLogEntries: transformedChangeLogEntries,
    setQueryVariables,
    total,
    desiredItemsToShow,
    setDesiredItemsToShow,
  });

  return {
    toggles,
    toggleAt,
    toggleAll,
    loading,
    loadingFilter,
    filteredLogsToShow,
    onNext,
    hasMore,
    dateRange,
    onDateRangeChange,
    onClearFilter,
    onFilterSubmit,
  };
}

function extractTargetingTermValues(edge: ChangeLogEntryEdge): TargetingChanges['targetingTermValues'] {
  const auditObject = edge?.node?.audits?.object;
  if (auditObject?.__typename === 'AuditLineItemV5') {
    return (auditObject as ChangeLogUnionObjectWithTerms<'AuditLineItemV5'>).lineItemTargetingTermValues ?? null;
  }
  if (auditObject?.__typename === 'AuditAdV5') {
    return (auditObject as ChangeLogUnionObjectWithTerms<'AuditAdV5'>).adTargetingTermValues ?? null;
  }
  return null;
}

export function mapTargetingChanges(
  changeLogResponse: GetChangeLogByAdObjectIdQuery | undefined
): TargetingChangesList {
  if (!changeLogResponse?.getChangeLogByAdObjectID?.edges.length) {
    return [];
  }

  const edges = changeLogResponse.getChangeLogByAdObjectID.edges as ChangeLogEntryEdge[];

  return edges.map((edge: ChangeLogEntryEdge) => {
    const targetingChangesMap = edge?.node?.targetingChanges ?? null;
    const targetingTermValues = extractTargetingTermValues(edge) ?? null;

    return { targetingChangesMap, targetingTermValues };
  });
}
